/* eslint-disable no-console */
/**
 * Dev-only client log shim.
 *
 * Captures console.* (incl. log), window error / unhandledrejection, React's
 * console.error overlay path, and fetch() round-trips, then batch-POSTs to
 * `/api/__devlog` where the dev server appends each entry as a JSON line to
 * `/tmp/checkout-devlog.jsonl`. Lets us read iPhone-Safari logs over the
 * tunnel without the Web Inspector.
 *
 * Hard-coded no-op in production via `process.env.NODE_ENV === 'production'`.
 * Self-loop guard: the wrapper never logs its own POST attempts or failures.
 */

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug' | 'fetch' | 'event';

interface LogEntry {
  t: number;            // performance.now() at capture
  iso: string;          // ISO timestamp
  level: LogLevel;
  msg: string;
  data?: unknown;
  stack?: string;
  path?: string;
  ua?: string;          // captured once per session in first entry
}

const BATCH_INTERVAL_MS = 400;
const MAX_BATCH = 50;
const ENDPOINT = '/api/__devlog';
const SELF_TAG = '[devlog]';

let installed = false;
let buffer: LogEntry[] = [];
let flushTimer: number | null = null;
let didEmitUA = false;

function safeJoin(args: unknown[]): string {
  return args
    .map((a) => {
      if (typeof a === 'string') return a;
      if (a instanceof Error) return `${a.name}: ${a.message}`;
      try {
        return JSON.stringify(a);
      } catch {
        try {
          return String(a);
        } catch {
          return '[unserializable]';
        }
      }
    })
    .join(' ');
}

function scheduleFlush() {
  if (flushTimer !== null) return;
  flushTimer = window.setTimeout(flush, BATCH_INTERVAL_MS);
}

function flush() {
  if (flushTimer !== null) {
    window.clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (buffer.length === 0) return;
  const batch = buffer;
  buffer = [];
  try {
    // keepalive so a tab close / navigation still flushes
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ entries: batch }),
      keepalive: true,
    }).catch(() => {
      // Never log our own POST failures — would loop.
    });
  } catch {
    // Same — swallow.
  }
}

function push(entry: LogEntry) {
  if (!didEmitUA) {
    didEmitUA = true;
    entry.ua = navigator.userAgent;
  }
  buffer.push(entry);
  if (buffer.length >= MAX_BATCH) {
    flush();
  } else {
    scheduleFlush();
  }
}

function makeEntry(level: LogLevel, msg: string, data?: unknown, stack?: string): LogEntry {
  return {
    t: Math.round(performance.now()),
    iso: new Date().toISOString(),
    level,
    msg,
    ...(data !== undefined ? { data } : {}),
    ...(stack ? { stack } : {}),
    path: typeof window !== 'undefined' ? window.location.pathname : undefined,
  };
}

export function installDevLog() {
  if (installed) return;
  if (typeof window === 'undefined') return;
  if (process.env.NODE_ENV === 'production') return;
  installed = true;

  // Wrap console.{log,info,warn,error,debug}
  (['log', 'info', 'warn', 'error', 'debug'] as const).forEach((level) => {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      const first = typeof args[0] === 'string' ? args[0] : '';
      // Skip our own breadcrumbs to avoid double-logging via console.warn.
      if (!first.startsWith(SELF_TAG)) {
        try {
          const msg = safeJoin(args);
          // Pull stack only for errors (cheaper).
          let stack: string | undefined;
          if (level === 'error') {
            const errArg = args.find((a) => a instanceof Error) as Error | undefined;
            stack = errArg?.stack;
          }
          push(makeEntry(level, msg, undefined, stack));
        } catch {
          // Never block the original console call.
        }
      }
      original(...args);
    };
  });

  // window error (uncaught throw)
  window.addEventListener('error', (event) => {
    try {
      push(
        makeEntry(
          'error',
          `[uncaught] ${event.message || 'unknown'}`,
          {
            filename: event.filename,
            line: event.lineno,
            col: event.colno,
          },
          event.error?.stack,
        ),
      );
      flush();
    } catch {
      // Swallow.
    }
  });

  // unhandled promise rejection
  window.addEventListener('unhandledrejection', (event) => {
    try {
      const reason = event.reason as unknown;
      const msg =
        reason instanceof Error
          ? `${reason.name}: ${reason.message}`
          : typeof reason === 'string'
          ? reason
          : safeJoin([reason]);
      push(
        makeEntry(
          'error',
          `[rejection] ${msg}`,
          undefined,
          reason instanceof Error ? reason.stack : undefined,
        ),
      );
      flush();
    } catch {
      // Swallow.
    }
  });

  // fetch instrumentation
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    let url = '';
    try {
      url =
        typeof input === 'string'
          ? input
          : input instanceof URL
          ? input.toString()
          : input instanceof Request
          ? input.url
          : String(input);
    } catch {
      url = '[unreadable-url]';
    }
    // Skip our own endpoint to avoid loops.
    if (url.includes(ENDPOINT)) {
      return originalFetch(input, init);
    }
    const method = init?.method ?? (input instanceof Request ? input.method : 'GET');
    const start = performance.now();
    try {
      const res = await originalFetch(input, init);
      const duration = Math.round(performance.now() - start);
      push(
        makeEntry('fetch', `${method} ${url} → ${res.status}`, {
          status: res.status,
          duration_ms: duration,
        }),
      );
      return res;
    } catch (e) {
      const duration = Math.round(performance.now() - start);
      push(
        makeEntry(
          'error',
          `[fetch failed] ${method} ${url}`,
          { duration_ms: duration },
          (e as Error)?.stack,
        ),
      );
      throw e;
    }
  };

  // Flush on page hide / unload so we don't lose tail entries when the user
  // navigates away mid-burst.
  window.addEventListener('pagehide', flush);
  window.addEventListener('beforeunload', flush);

  // Heartbeat so we can spot a silent freeze (no entries for X seconds after
  // a known event).
  push(makeEntry('event', `${SELF_TAG} installed`, { node_env: process.env.NODE_ENV }));
}

/**
 * Targeted breadcrumb. Use sparingly in suspected hot paths so the log
 * highlights what we're chasing.
 */
export function dbg(event: string, data?: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  if (process.env.NODE_ENV === 'production') return;
  push(makeEntry('event', `${SELF_TAG} ${event}`, data));
}
